/* eslint-disable @typescript-eslint/naming-convention */
import { lazy, Suspense } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Button, HStack } from '@chakra-ui/react'
import { useBlocker } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { cloneDeep, isEqual } from 'lodash'

import { useEventFormHydration } from '@hooks/useEventFormHydration'

import { EventFormData } from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'
import { useSelectedEventContext } from '@contexts/SelectedEventContext'

import { useMatchEventEditorData } from '@queries/useMatchEventEditorData'

import {
    EventTypeSelect,
    ExtraInfoRadioGroup,
    MatchEventExtraInfoSelect,
    PeriodSelect,
    PlayersSelectorGroup,
    ResultInput,
    ReviewDetailsSelectorGroup,
    TeamSelect,
    TimeInputGroup,
} from './FormFields'
import { PenaltyNumberInput } from './FormFields/PenaltyNumberInput'
import { formatMatchTime, getEventFormPreservedValues, getPlayerIdFromOption, getPlayerNameFromOption } from './utils'

const ReactHookFormDevTools =
    process.env.NODE_ENV === 'production'
        ? () => null
        : lazy(() =>
              import('@hookform/devtools').then(res => ({
                  default: res.DevTool,
              })),
          )

export function EventForm() {
    const { selectedEvent } = useSelectedEventContext()
    const { update, create } = useMatchEventEditorData()
    const {
        resetForm,
        isFormValid,
        zodForm,
        isAdditionalTimeSelected,
        isFormDisabled,
        isExtraInfoRequired,
        isPenaltyOrderRequired,
        isResultRequired,
    } = useMatchEditorFormContext()

    useEventFormHydration()

    useBlocker(
        () => window.confirm('Are you sure you want to leave? You have unsaved changes!'),
        zodForm.formState.isDirty,
    )

    const prepareRequestPayload = (formData: EventFormData) => ({
        team_id: formData.team_id?.value ? formData.team_id.value : null,
        type: Number(formData.type?.value),
        time: formatMatchTime(formData.time, isAdditionalTimeSelected),
        timestamp: format(parseISO(selectedEvent?.time ?? new Date().toISOString()), 'T'),
        player1_id: getPlayerIdFromOption(formData.player1_id),
        player1_name: getPlayerNameFromOption(formData.player1_id),
        player2_id: getPlayerIdFromOption(formData.player2_id),
        player2_name: getPlayerNameFromOption(formData.player2_id),
        team1_score: isResultRequired ? Number(formData.team1_score || 0) : null,
        team2_score: isResultRequired ? Number(formData.team2_score || 0) : null,
        extra_info:
            isExtraInfoRequired && !Number.isNaN(Number(formData.extra_info)) ? Number(formData.extra_info) : null,
        penalty_order: isPenaltyOrderRequired ? Number(formData.penalty_order) : null,
        period_number: Number(formData.period_number?.value),
        review_details: {
            original_decision: !Number.isNaN(Number(formData.review_original_decision?.value))
                ? Number(formData.review_original_decision?.value)
                : null,
            outcome: !Number.isNaN(Number(formData.review_outcome?.value))
                ? Number(formData.review_outcome?.value)
                : null,
        },
    })

    const handleOnSubmit: SubmitHandler<EventFormData> = async formData => {
        const isEdit = Boolean(formData.id ?? selectedEvent?.id)
        const eventId = formData.id ?? selectedEvent?.id

        const submittedValues = cloneDeep(zodForm.getValues())
        const requestPayload = prepareRequestPayload(formData)

        const resetIfUnchanged = () => {
            const currentValues = zodForm.getValues()
            if (isEqual(currentValues, submittedValues)) {
                resetForm(getEventFormPreservedValues(formData))
            }
        }

        if (isEdit && eventId) {
            await update(
                { id: String(eventId), ...requestPayload },
                {
                    onSuccess: () => {
                        resetIfUnchanged()
                    },
                },
            )

            return
        }

        await create(requestPayload, {
            onSuccess: () => {
                resetIfUnchanged()
            },
        })
    }

    const buttonLabel = selectedEvent?.id ? 'Save Event' : 'Add Event'

    return (
        <form noValidate autoComplete="off" onSubmit={zodForm.handleSubmit(handleOnSubmit)}>
            <TeamSelect />
            <HStack>
                <EventTypeSelect />
                <MatchEventExtraInfoSelect />
            </HStack>
            <PeriodSelect />
            <TimeInputGroup />
            <HStack alignItems="flex-start" w="100%">
                <PenaltyNumberInput />
                <PlayersSelectorGroup />
            </HStack>
            <ResultInput />
            <ReviewDetailsSelectorGroup />
            <ExtraInfoRadioGroup />
            <Button
                mt={8}
                colorScheme="teal"
                isLoading={zodForm.formState.isSubmitting}
                isDisabled={!isFormValid || isFormDisabled}
                type="submit">
                {buttonLabel}
            </Button>
            <Suspense fallback={null}>
                {/* @ts-expect-error I am not typing internal props for React Hook Form like _reset, nonsense */}
                <ReactHookFormDevTools control={zodForm.control} />
            </Suspense>
        </form>
    )
}
